import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAppContext } from '@/Main';
import { getCookie, removeCookie } from '@/app/common/utils/cookieService';
import {
  areFilterStatesEqual,
  parseQueryString
} from '@/utils/queryString';
import { setFilters } from '@/app/common/reducers/filterSlice';
import classNames from 'classnames';
import NavbarTop from '@/components/navbar/top/NavbarTop';
import NavbarVertical from '@/components/navbar/vertical/NavbarVertical';
import Footer from '@/components/footer/Footer';
import { useDispatch, useSelector } from 'react-redux';
import { addRecent } from '@/app/common/reducers/recentVisitSlice';
import { toolRoutes } from '@/routes/siteMaps';
import { ErrorBoundary } from 'react-error-boundary';
import Error500 from '@/components/errors/Error500';
import { breakpointsMediaQuery, useMediaQuery } from '@/helpers/utils';
import NoticeTop from '@/components/common/NoticeTop';
import Maintenance from '@/components/errors/Maintenance';
import { useGetNotifsQuery } from '@/app/common/api/notifApi';
import NotifModal from '@/app/common/components/NotifModal';
import AiSupportFab from '@/components/navbar/AiSupportFab';

type MainLayoutRootState = {
  selectedDomain: { name: string };
  filters: Record<string, unknown> | null;
};

const MainLayout = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { hash, pathname } = location;
  const isKanban = pathname.includes('kanban');
  const isAssistantSurface =
    pathname.startsWith('/chat') || pathname.startsWith('/mcp/connect');
  const access_token = getCookie(import.meta.env.VITE_ACCESS_TOKEN_NAME);
  const refresh_token = getCookie(import.meta.env.VITE_REFRESH_TOKEN_NAME);
  const selectedDomain = useSelector(
    (state: MainLayoutRootState) => state.selectedDomain
  );
  const filters = useSelector((state: MainLayoutRootState) => state.filters);

  const {
    config: { isFluid, navbarPosition }
  } = useAppContext();

  useEffect(() => {
    if (location.search.includes('FBy')) {
      const queryObjects = parseQueryString(location.search);
      // Compare semantic filter content (not raw query strings).
      // String compare broke on "+" vs space and caused React #185.
      // Do not depend on `filters` here: clearing Redux while FBy is still in
      // the URL would re-hydrate filters and leave filter chips stuck after Reset.
      if (
        Object.keys(queryObjects).length > 0 &&
        !areFilterStatesEqual(filters, queryObjects)
      ) {
        dispatch(setFilters(queryObjects));
      }
    } else if (filters && Object.keys(filters).length > 0) {
      dispatch(setFilters({}));
    }

    setTimeout(() => {
      if (hash) {
        const id = hash.replace('#', '');
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView({ block: 'start', behavior: 'smooth' });
        }
      }
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync from URL only; see comment above
  }, [location.search, hash, dispatch]);

  const routeFounder = (url: string) => {
    for (let i = 0; i < toolRoutes?.children?.length; i++) {
      if (toolRoutes?.children.at(i)?.children) {
        for (let j = 0; j < toolRoutes.children[i].children!.length; j++) {
          if (url.includes(toolRoutes.children[i].children![j].to)) {
            const { name, to } = toolRoutes.children[i].children![j];
            return { name, to };
          }
        }
      } else {
        const child = toolRoutes.children.at(i)!;
        const { name, to } = child;
        return { name, to };
      }
    }
    return undefined;
  };

  useEffect(() => {
    const route = routeFounder(pathname);
    if (route && pathname.includes(selectedDomain.name)) {
      dispatch(
        addRecent({
          url: pathname,
          route,
          domain: selectedDomain
        })
      );
    }
    window.scrollTo(0, 0);
  }, [pathname, selectedDomain, dispatch]);

  const isMdUp = useMediaQuery(breakpointsMediaQuery.md);
  const { data, isFetching, isError } = useGetNotifsQuery({
    filter: { type: 'pop_up', is_active: 'true' },
    sort: '-createdAt'
  });

  if (refresh_token && access_token) {
    return (
      <div className={isFluid ? 'container-fluid' : 'container'}>
        {import.meta.env.VITE_SITE_REPAIRING === 'yes' ? (
          <Maintenance />
        ) : (
          <>
            {navbarPosition === 'top' && isMdUp ? '' : <NavbarVertical />}
            {!isError &&
              !isFetching &&
              data?.data &&
              data?.data?.length > 0 && (
                <NotifModal popUpData={data?.data[0]} />
              )}
            <div className={classNames('content', { 'pb-0': isKanban || isAssistantSurface })}>
              <NavbarTop />
              <div className={classNames({ 'mb-7': !isAssistantSurface })}>
                {!isAssistantSurface && <NoticeTop />}
                <ErrorBoundary FallbackComponent={Error500} onReset={() => {}}>
                  <Outlet />
                </ErrorBoundary>
              </div>
              {!isAssistantSurface && <Footer />}
            </div>
            <AiSupportFab />
          </>
        )}
      </div>
    );
  }

  removeCookie(import.meta.env.VITE_ACCESS_TOKEN_NAME, true);
  removeCookie(import.meta.env.VITE_REFRESH_TOKEN_NAME, true);
  const next = `${pathname}${location.search}`;
  return (
    <Navigate
      to={`/login?next=${encodeURIComponent(next)}`}
      replace
    />
  );
};

export default MainLayout;
